import React, { useState, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
  TextField,
  Chip,
  Link,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import Autocomplete from '@mui/material/Autocomplete';
import Iconify from 'src/components/iconify';
import Animation from 'src/assets/animations/components/animation-wrapper';
import successDataAnimation from 'src/assets/animations/json/success-tick.json';
import { sendBulkJobInvite } from 'src/services/role.service';
import {
  RHFTextField,
} from 'src/components/hook-form';

const InviteCandidatesForm = ({ isOpen, onClose, orgId, jobId }) => {
  const defaultValues = useMemo(
    () => ({
      recipients: [],
      subject: 'Job Application Invitation',
      description: `Dear Candidate,
      
We are excited to invite you to apply for the position of {jobName} at {companyName}. After reviewing your profile, we believe you have the skills and experience that align perfectly with our team.
        
To apply, please click the link below and complete the application form:

We look forward to receiving your application.
          
Best regards,
          
{clientName}
          
{companyName}`
    }),
    [],
  );

  const Schema = Yup.object().shape({
    recipients: Yup.array().of(Yup.string().email('Invalid email')).min(1, 'At least one recipient is required'),
    subject: Yup.string().required('Subject is required'),
    description: Yup.string().required('Email Description is required'),
  });

  const methods = useForm({
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const { handleSubmit, control, trigger } = methods;

  const [tagInputValue, setTagInputValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [emails, setEmails] = useState([]);

  const handleOnModalClose = () => {
    onClose();
    setIsSubmitting(false);
    setShowConfirmationMessage(false);
  };

  const handleSendClick = async () => {
    const isValid = await trigger();
    if (isValid) {
      setShowConfirmationMessage(true);
    } else {
      console.log("Invalid");
    }
  };

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      const payload = {
        email_list: data?.recipients,
        invite_type: "EMAIL_LIST",
        excel_file_location: "",
      };
      console.log(payload);
      const response = await sendBulkJobInvite(payload, orgId, jobId);
      if (response.success) {
        setShowSuccessMessage(true);
        setShowConfirmationMessage(false);
        methods.setValue('recipients', []);
        setEmails([]);
        setIsSubmitting(false);
        setTimeout(() => {
          setShowSuccessMessage(false);
          handleOnModalClose();
        }, 3000);
      } else {
        handleOnModalClose();
        throw new Error(response.message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <FormProvider {...methods}>
      <Dialog
        open={isOpen}
        onClose={handleOnModalClose}
        maxWidth
      >
        <DialogContent>
          {showSuccessMessage ? (
            <Stack
              spacing="25px"
              width="760px"
              p={8}
              alignItems="center"
              justifyContent="center"
              textAlign="center">
              <Animation animationData={successDataAnimation} width={160} height={160} />
              <Stack spacing="8px">
                <Typography variant="h4" mb={1}>
                  Email Invites Sent Successfully
                </Typography>
                <Typography variant="input" color="content.primaryBlack3">
                  Email invites will be sent to the email address listed in approx 2 -3 mins
                </Typography>
              </Stack>
            </Stack>
          ) :
            showConfirmationMessage ? (
              <Stack spacing="17px" width="360px">
                <Iconify width={48} icon="tabler:mail-share" />
                <Stack spacing="8px" >
                  <Typography variant="h4" mb={1}>
                    Sending Email Invitation
                  </Typography>
                  <Typography variant="input" color="content.primaryBlack3">
                    Are you sure you want to send invite emails?
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Stack spacing="16px" width="760px">
                <Stack sx={{ display: 'flex', paddingX: '20px' }} >
                  <Typography variant="subtitle1" fontWeight={500}>
                    {emails.length} {emails.length <= 1 ? 'candidate will be sent an email' : 'candidates will be sent an email'}
                  </Typography>
                </Stack>
                <Stack
                  sx={{ display: 'flex', marginTop: '10px' }}
                  justifyContent="start"
                  alignItems="start"
                  direction="row"
                  spacing="10px">
                  <Iconify height="100%" icon="tabler:mail-share" width={35} />
                  <Typography variant="h2" fontWeight={600} marginY="auto">
                    Invite Candidates
                  </Typography>
                </Stack>
                <Stack spacing="6px">
                  <Typography variant="subtitle1">To</Typography>
                  <Controller
                    name="recipients"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        {...field}
                        inputValue={tagInputValue}
                        onInputChange={(event, newInputValue) => {
                          if (/[\s,]/.test(newInputValue)) {
                            const newEmails = newInputValue.split(/[\s,]+/).map(email => email.trim()).filter(email => email);
                            const uniqueEmails = [...new Set([...field.value, ...newEmails])];
                            field.onChange(uniqueEmails);
                            setEmails(uniqueEmails);
                            setTagInputValue('');
                          } else {
                            setTagInputValue(newInputValue);
                          }
                        }}
                        onChange={(event, newValue) => {
                          field.onChange(newValue);
                          setEmails(newValue);
                        }}
                        multiple
                        freeSolo
                        options={[]}
                        renderTags={(selected, getTagProps) => (
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 1,
                              maxHeight: '7em',
                              overflowY: 'auto',
                            }}
                          >
                            {selected.map((option, index) => (
                              <Chip
                                {...getTagProps({ index })}
                                key={option}
                                label={option}
                                size="small"
                                color="info"
                                variant="soft"
                                sx={{
                                  borderRadius: '5px',
                                }}
                              />
                            ))}
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Email id of the candidates"
                            error={!!error}
                            helperText={error ? error?.message : null}
                            onPaste={(event) => {
                              event.preventDefault();
                              const pastedData = event.clipboardData.getData('Text');
                              const newEmails = pastedData.split(/[\s,]+/).map(email => email.trim()).filter(email => email);
                              const uniqueEmails = [...new Set([...field.value, ...newEmails])];
                              field.onChange(uniqueEmails);
                              setEmails(uniqueEmails);
                            }}
                          />
                        )}
                      />
                    )}
                  />
                </Stack>
                <Stack spacing="6px">
                  <Typography variant="subtitle1">Subject</Typography>
                  <RHFTextField name="subject" disabled />
                </Stack>
                <Stack spacing="6px">
                  <Typography variant="subtitle1">Body</Typography>
                  <RHFTextField name="description" multiline rows={14} placeholder="Email Description" disabled />
                </Stack>
                {/* <Stack >
                    <Typography variant="subtitle1" fontWeight={500}>To change the above content. Go back to the <Link color={theme => theme.palette.content.accent1} underline="none" href="#">Message Template.</Link></Typography>
                </Stack> */}
              </Stack>
            )}
        </DialogContent>
        <DialogActions>
          {showSuccessMessage ? (
            null
          ) :
            showConfirmationMessage ? (
              <Stack direction="row" alignItems="center" spacing="12px" width={'100%'}>
                <Button
                  variant="soft"
                  onClick={() => {
                    setShowConfirmationMessage(false);
                  }}
                  fullWidth
                >
                  Cancel
                </Button>
                <LoadingButton
                  loading={isSubmitting}
                  variant="contained"
                  onClick={handleSubmit(onSubmit)}
                  fullWidth
                >
                  Yes
                </LoadingButton>
              </Stack>
            ) : (
              <Stack
                direction="row"
                width="100%"
                justifyContent="space-between"
              >
                <Button onClick={handleOnModalClose} variant="soft" fullWidth sx={{ marginRight: '10px' }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSendClick}
                  fullWidth
                >
                  Send
                </Button>
              </Stack>
            )
          }
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export default InviteCandidatesForm;
